import { Box, Typography, Divider, CircularProgress } from "@mui/joy";
import { Plus } from "lucide-react";
import { useState } from "react";

import { DataGridSetting } from "../../../../../../components";
import { Button } from "../../../../../../shared";
import { useManufacturerProduct } from "../../../../../../hooks/query";

import { columnDef } from "./column-def";
import { StatusCellRenderer, Info } from "./GridFrameworks";
import ProductForm from "./ProductForm";

import type { IManufacturerProduct } from "../../../../../../interfaces";

interface ProductOnboardingProps {
    manufacturerId: string;
    canManage?: boolean;
}

export function ProductOnboarding({ manufacturerId, canManage = false }: ProductOnboardingProps) {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<IManufacturerProduct | null>(null);

    const { useList } = useManufacturerProduct();
    const { data, isLoading, isError, error } = useList(manufacturerId);

    const handleCreate = () => {
        if (!canManage) return;
        setEditingProduct(null);
        setIsFormOpen(true);
    };

    const handleEdit = (product: IManufacturerProduct) => {
        if (!canManage) return;
        setEditingProduct(product);
        setIsFormOpen(true);
    };

    const handleClose = () => {
        setIsFormOpen(false);
        setEditingProduct(null);
    };

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={2}>
                <Box sx={{ color: "danger.plainColor" }}>
                    {error instanceof Error ? error.message : "Failed to load products"}
                </Box>
            </Box>
        );
    }

    const products = (data as IManufacturerProduct[]) || [];

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
            <Box ml={2} mt={2} mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                        Product Onboarding
                    </Typography>
                    <Typography level="body-sm" textColor="text.tertiary">
                        {canManage
                            ? "Manage products for this manufacturer."
                            : "Products for this manufacturer."}
                    </Typography>
                </Box>
            </Box>
            <Divider />
            <Box flex={1} sx={{ overflow: "hidden" }}>
                <DataGridSetting
                    gridData={products}
                    quickControlStartDecorator={
                        canManage ? (
                            <Box display="flex" alignItems={"center"} ml={1.5}>
                                <Button
                                    variant="soft"
                                    color="primary"
                                    onClick={handleCreate}
                                    startDecorator={<Plus size={18} />}
                                >
                                    Add Product
                                </Button>
                            </Box>
                        ) : undefined
                    }
                    columnDefs={columnDef
                        .filter((col) => canManage || col.cellRenderer !== "Info")
                        .map((col) => {
                            if (col.cellRenderer === "StatusCellRenderer") {
                                return { ...col, cellRenderer: StatusCellRenderer };
                            }
                            if (col.cellRenderer === "Info") {
                                return {
                                    ...col,
                                    cellRenderer: Info,
                                    cellRendererParams: { onEdit: handleEdit },
                                };
                            }
                            return col;
                        })}
                />
            </Box>

            {canManage && isFormOpen ? (
                <ProductForm manufacturerId={manufacturerId} product={editingProduct} onClose={handleClose} />
            ) : null}
        </Box>
    );
}
